// Sprint Mix: React UI on the ToolLayout template. The maths lives in ./model.js. It is
// the hub of the planning tools: the Sprint Forecast sets capacity, the Tech Debt
// Payback calculator adds Improve items and the Learning Backlog adds Learn items, each
// returning here with this plan intact (src/lib/handBack.js).

import { useMemo, useState } from "react";
import { ToolLayout } from "@/components/templates";
import {
  BarFooter,
  BriefLines,
  Callout,
  DataTable,
  ExampleDataPicker,
  Explainer,
  Field,
  FieldGrid,
  MetricCard,
  MetricGrid,
  Panel,
  RangeInput,
  UrlStateNotices,
  useUrlRestore,
  useUrlSync,
  type Range,
} from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { headingFor, ledeOf } from "@/lib/pageHeadings";
import { linkWithBack } from "@/lib/handBack.js";
import { KINDS, KIND_LABELS, MAX_ITEMS, TEAMS, TEAM_LABELS, analyseMix, fmt, pct, summaryLines } from "./model.js";
import { EXAMPLES, exampleById } from "./examples.js";
import { CAPACITY_UNITS, sprintMixShare } from "./share.js";
import { DEFAULT_STATE as SPRINTS_DEFAULT, sprintsShare } from "@/sprints/share.js";

const HEADING = headingFor("tool/sprint-planner");

/* eslint-disable @typescript-eslint/no-explicit-any */
type Any = any;

interface Item {
  label: string;
  kind: string;
  team: string;
  value: Range;
  valueIn: string;
  size: Range;
}
interface State {
  valueUnit: string;
  capacityUnit: string;
  dayValue: number;
  capacity: Record<string, Range>;
  items: Item[];
}

const INITIAL = EXAMPLES[0].state as State;
const numberOr = (text: string, fallback: number) => (text.trim() === "" || !Number.isFinite(Number(text)) ? fallback : Number(text));

export function App() {
  const shared = useUrlRestore<State>(sprintMixShare, (s) => setState(s));
  const [state, setState] = useState<State>(shared.initial ?? INITIAL);
  useUrlSync(sprintMixShare, () => state, [state], shared);
  const set = (patch: Partial<State>) => setState((s) => ({ ...s, ...patch }));
  const setItem = (i: number, patch: Partial<Item>) => setState((s) => ({ ...s, items: s.items.map((x, k) => (k === i ? { ...x, ...patch } : x)) }));
  const r: Any = useMemo(() => analyseMix(state), [state]);
  const vu = state.valueUnit;
  const cu = state.capacityUnit;
  const forecastLink = linkWithBack("/tool/sprint-forecast", sprintMixShare, state, {
    codec: sprintsShare,
    state: { ...SPRINTS_DEFAULT, unit: cu },
  });

  return (
    <ToolLayout
      label="Unit 41 / Sprint mix"
      tags={<span className="hidden md:inline">Local · nothing leaves the page</span>}
      tag="SPRINT MIX"
      title={HEADING.h1}
      tagline={ledeOf(HEADING)}
      mainClassName="mx-auto w-full max-w-[1280px] p-6"
      footer={
        <BarFooter>
          <span>Expected value per unit of size · simulated sizes and capacity</span>
          <span>
            {r.items.length} items · sprint worth {fmt(r.plan.mean)} {vu}
          </span>
        </BarFooter>
      }
    >
      <Explainer id="sprintmix" title="What this answers" summary="What goes in the sprint, across build and discovery" defaultOpen>
        <p>
          A sprint mixes <strong>Deliver</strong> items (features), <strong>Learn</strong> items (research and
          experiments) and <strong>Improve</strong> items (refactoring, tooling). Put a value range and a size range
          on each, in one value unit, and the page orders them by expected value per unit of size.
        </p>
        <p>
          Two teams fill their own capacity: <strong>build</strong> (engineers and coding agents) and{" "}
          <strong>discovery</strong> (design and product). Sizes and capacity are uncertain, so the page gives the
          chance each item gets done — and marks the items whose place would change if you knew their value.
        </p>
      </Explainer>

      <Panel step={1} title="Start from an example" hint="Or build your sprint in 02 and 03">
        <UrlStateNotices state={shared} />
        <ExampleDataPicker
          examples={EXAMPLES.map(({ id, name, description }) => ({ id, name, description }))}
          onLoad={(id) => {
            const ex = exampleById(id);
            if (ex) setState(ex.state as State);
          }}
          deepLink={!shared.initial}
        />
        <p className="mb-0 mt-3 text-xs text-muted-foreground">Examples are illustrative: an invented startup&rsquo;s sprint.</p>
      </Panel>

      <Panel step={2} title="Teams and units" hint="Capacity as a 90% range per team">
        <FieldGrid>
          <Field label="Value unit" hint="e.g. €k, signups">
            <Input aria-label="Value unit" maxLength={20} value={vu} onChange={(e) => set({ valueUnit: e.target.value })} />
          </Field>
          <Field label="Size unit">
            <Select aria-label="Size unit" value={cu} onChange={(e) => set({ capacityUnit: e.target.value })}>
              {CAPACITY_UNITS.map((x) => (
                <option key={x} value={x}>
                  {x}
                </option>
              ))}
            </Select>
          </Field>
          {TEAMS.map((t: string) => (
            <Field key={t} label={`${(TEAM_LABELS as Record<string, string>)[t]} capacity`} hint={t === "build" ? "Engineers and coding agents" : "Design and product"}>
              <RangeInput label={`${(TEAM_LABELS as Record<string, string>)[t]} capacity`} min={0} value={state.capacity[t]} onChange={(v) => set({ capacity: { ...state.capacity, [t]: v } })} unit={cu} />
            </Field>
          ))}
          <Field label="Value of one team-day" hint={`In ${vu}, for items valued in team-days`}>
            <Input type="number" min={0} step="any" aria-label="Value of one team-day" value={String(state.dayValue)} onChange={(e) => set({ dayValue: Math.max(0, numberOr(e.target.value, 0)) })} />
          </Field>
        </FieldGrid>
        <p className="mb-0 mt-3 text-sm">
          <a href={forecastLink}>Forecast build capacity from past sprints →</a>
        </p>
        {r.missingCapacity.length > 0 && (
          <Callout kind="warn" className="mt-4">
            No usable capacity for {r.missingCapacity.map((t: string) => (TEAM_LABELS as Record<string, string>)[t].toLowerCase()).join(" and ")}: its items cannot be planned.
          </Callout>
        )}
      </Panel>

      <Panel step={3} title="Items" hint="Value and size as 90% ranges">
        <DataTable<Item>
          rows={state.items}
          rowKey={(_, i) => i}
          rowClassName={(_, i) => (r.excluded.some((e: Any) => e.index === i) ? "bg-destructive/10" : undefined)}
          columns={[
            { head: "Item", cell: (x, i) => <Input aria-label={`Item ${i + 1} name`} className="h-8 min-w-[11rem] rounded-none" value={x.label} onChange={(e) => setItem(i, { label: e.target.value })} /> },
            {
              head: "Kind",
              cell: (x, i) => (
                <Select aria-label={`Item ${i + 1} kind`} value={x.kind} onChange={(e) => setItem(i, { kind: e.target.value })}>
                  {KINDS.map((k: string) => (
                    <option key={k} value={k}>
                      {(KIND_LABELS as Record<string, string>)[k]}
                    </option>
                  ))}
                </Select>
              ),
            },
            {
              head: "Team",
              cell: (x, i) => (
                <Select aria-label={`Item ${i + 1} team`} value={x.team} onChange={(e) => setItem(i, { team: e.target.value })}>
                  {TEAMS.map((t: string) => (
                    <option key={t} value={t}>
                      {(TEAM_LABELS as Record<string, string>)[t]}
                    </option>
                  ))}
                </Select>
              ),
            },
            {
              head: "Value",
              cell: (x, i) => (
                <span className="inline-flex flex-wrap items-center gap-1.5">
                  <RangeInput label={`Item ${i + 1} value`} value={x.value} onChange={(value) => setItem(i, { value })} />
                  <Select aria-label={`Item ${i + 1} value unit`} className="h-8 w-auto" value={x.valueIn} onChange={(e) => setItem(i, { valueIn: e.target.value })}>
                    <option value="value">{vu}</option>
                    <option value="days">team-days</option>
                  </Select>
                </span>
              ),
            },
            { head: `Size (${cu})`, cell: (x, i) => <RangeInput label={`Item ${i + 1} size`} min={0} value={x.size} onChange={(size) => setItem(i, { size })} /> },
            {
              head: <span className="sr-only">Remove</span>,
              cell: (x, i) => (
                <Button variant="outline" size="sm" aria-label={`Remove ${x.label || `item ${i + 1}`}`} onClick={() => setState((s) => ({ ...s, items: s.items.filter((_, k) => k !== i) }))}>
                  ×
                </Button>
              ),
            },
          ]}
        />
        <div className="mt-3 flex flex-wrap items-center gap-4">
          <Button
            disabled={state.items.length >= MAX_ITEMS}
            onClick={() =>
              setState((s) => ({ ...s, items: [...s.items, { label: `Item ${s.items.length + 1}`, kind: "deliver", team: "build", value: { low: 0, high: 10 }, valueIn: "value", size: { low: 2, high: 5 } }] }))
            }
          >
            + Add item
          </Button>
          <a className="text-sm" href={linkWithBack("/tool/tech-debt-calculator", sprintMixShare, state)}>
            Add a tech debt fix →
          </a>
          <a className="text-sm" href={linkWithBack("/tool/learning-backlog", sprintMixShare, state)}>
            Add research from the Learning Backlog →
          </a>
        </div>
        {r.excluded.length > 0 && (
          <Callout kind="warn" className="mt-4">
            <strong>Not planned:</strong>
            <ul className="mb-0 mt-1 pl-5">
              {r.excluded.map((e: Any) => (
                <li key={e.index}>
                  {e.label || `Item ${e.index + 1}`}: {e.reasons.join(" ")}
                </li>
              ))}
            </ul>
          </Callout>
        )}
      </Panel>

      <Panel step={4} title="The plan" hint="Conditional on the ranges in 02 and 03">
        <MetricGrid className="mb-6 lg:grid-cols-3">
          <MetricCard accent eyebrow="Sprint worth, expected" value={`${fmt(r.plan.mean)} ${vu}`} data-value={r.plan.mean}>
            90% range {fmt(r.plan.p5)} to {fmt(r.plan.p95)} {vu}.
          </MetricCard>
          <MetricCard eyebrow="Chance the sprint gains" value={pct(r.plan.chancePositive)}>
            Chance the done items are worth more than 0 together.
          </MetricCard>
          <MetricCard eyebrow="Capacity by kind" value={KINDS.map((k: string) => `${(KIND_LABELS as Record<string, string>)[k][0]} ${fmt(r.mix[k])}`).join(" · ")}>
            Expected {cu} done on Deliver, Learn and Improve items.
          </MetricCard>
        </MetricGrid>
        {TEAMS.map((t: string) => {
          const rows = r.items.filter((it: Any) => it.team === t).sort((a: Any, b: Any) => (a.priority ?? 999) - (b.priority ?? 999));
          if (!rows.length) return null;
          return (
            <DataTable<Any>
              key={t}
              className="mb-6"
              caption={`${(TEAM_LABELS as Record<string, string>)[t]} team, in order`}
              rows={rows}
              rowKey={(it) => it.index}
              rowClassName={(it) => (it.worthDoing ? undefined : "text-muted-foreground")}
              columns={[
                { head: "Order", numeric: true, cell: (it) => (it.priority ? String(it.priority).padStart(2, "0") : "—") },
                { head: "Item", cell: (it) => it.label },
                { head: "Kind", cell: (it) => (KIND_LABELS as Record<string, string>)[it.kind] },
                { head: `Value (${vu})`, numeric: true, cell: (it) => fmt(it.expectedValue) },
                { head: `Size (${cu})`, numeric: true, cell: (it) => fmt(it.expectedSize) },
                { head: "Value per unit", numeric: true, cell: (it) => fmt(it.valuePerSize) },
                { head: "Chance done", numeric: true, cell: (it) => (it.worthDoing ? pct(it.pDone) : "not worth doing") },
                {
                  head: "If you knew more",
                  cell: (it) => (it.unsettled ? <b>Place could change</b> : <span className="text-muted-foreground">Stable</span>),
                },
              ]}
            />
          );
        })}
        <Callout kind="note">
          <strong>Value of knowing:</strong> planning with every value and size known would add about {fmt(r.valueOfKnowing)} {vu} to this sprint on
          average. Items marked &ldquo;place could change&rdquo; are where research would pay — the Learning Backlog prices it.
        </Callout>
        <BriefLines className="mt-6" lines={summaryLines(r, state)} />
      </Panel>
    </ToolLayout>
  );
}
